import { buildTranslate } from 'locales'
import flatMap from 'lodash/flatMap'
import truncate from 'lodash/truncate'
import {
  CouponPoolGenerationType,
  CouponPoolType,
  Fields as CouponPool,
} from 'models/CouponPool'
import { Fields as MerchantFields } from 'models/Merchant'
import { Fields as MerchantCouponPool } from 'models/MerchantCouponPool'
import type { Fields as Modifier } from 'models/Modifier'
import { Fields as Product } from 'models/Product'
import {
  Fields as RedeemTemplate,
  MultiUseSettings,
  RedeemTemplateSubtype,
  RedeemType,
} from 'models/RedeemTemplate'
import { ProductIdType, RedeemProductIdsType } from 'models/RedeemTemplate'
import { RedemptionProvider } from 'models/RedemptionProvider'
import { ValidationTypes } from 'models/Validation'
import moment from 'moment'
import { I18n } from 'react-redux-i18n'
import type {
  MultiUseRewardsSettings,
  PosFields,
} from 'scenes/RedeemManager/Builder/FormModel'
import { getFormattedDate } from 'utilities/date'
import { DiscountScope, FormModel } from './Builder/FormModel'
import { InstoreRedemptionType, RedeemCategory, RedeemTypeItem } from './types'

const t = buildTranslate('redeem_manager.helpers')
const typesT = buildTranslate('redeem_manager.types')

export const FREE_VARIANT_KEYS = ['free_fixed_price_item'] as const

export function isFreeVariantKey(key: string | undefined): boolean {
  return FREE_VARIANT_KEYS.includes(key as typeof FREE_VARIANT_KEYS[number])
}

export function isFixedPriceKey(key: string | undefined): boolean {
  return (
    key === 'any_item_for_$x' ||
    key === 'any_modifier_for_$x' ||
    isFreeVariantKey(key)
  )
}

const FREE_SCOPE_MAP: Partial<Record<DiscountScope, DiscountScope>> = {
  free_base_item: 'base_item',
  free_base_item_plus_all_modifiers: 'base_item_plus_all_modifiers',
  free_base_item_plus_required_modifiers: 'base_item_plus_required_modifiers',
  free_base_item_plus_discounted_modifiers:
    'base_item_plus_discounted_modifiers',
}

export function getEffectiveDiscountScope(
  scope: DiscountScope | undefined
): DiscountScope | undefined {
  if (!scope) return scope
  return FREE_SCOPE_MAP[scope] ?? scope
}

// Note shown under a free item's modifier restrictions, for the scopes where
// the restrictions only qualify the reward ("just the item" and "all
// modifiers"). Undefined for every other type and scope.
export function getFreeItemRestrictionNoteKey(
  key: string | undefined,
  scope: DiscountScope | undefined
): string | undefined {
  if (!isFreeVariantKey(key)) return undefined
  switch (getEffectiveDiscountScope(scope)) {
    case 'base_item':
      return 'free_restriction_note_base_item'
    case 'base_item_plus_all_modifiers':
      return 'free_restriction_note_all_modifiers'
    default:
      return undefined
  }
}

// Line under a free item's Free items field, for the scopes where the default
// "covers the full cost, including modifiers" isn't true. Undefined for every
// other type and scope, which keep the default.
export function getFreeItemsNoteKey(
  key: string | undefined,
  scope: DiscountScope | undefined
): string | undefined {
  if (!isFreeVariantKey(key)) return undefined
  switch (getEffectiveDiscountScope(scope)) {
    case 'base_item':
      return 'free_items_note_base_item'
    case 'base_item_plus_discounted_modifiers':
      return 'free_items_note_discounted_modifiers'
    default:
      return undefined
  }
}

// True when two id lists hold the same ids, ignoring order and duplicates.
export function sameIds(
  a: string[] | undefined,
  b: string[] | undefined
): boolean {
  const left = new Set(a ?? [])
  const right = new Set(b ?? [])
  return left.size === right.size && Array.from(left).every(id => right.has(id))
}

// True for the scopes that discount the item plus the cheapest of its
// product_ids.<venue>.discounted_modifiers (free and fixed-price variants).
export function isModifierDiscountScope(
  scope: DiscountScope | undefined
): boolean {
  return (
    getEffectiveDiscountScope(scope) === 'base_item_plus_discounted_modifiers'
  )
}

// The modifier discounts a venue actually saves: none outside the modifier
// discount scopes, the restrictions in the default view, and the separate list
// in advanced mode. Autosave and the builder's summaries share this rule.
export function getSavedModifierDiscountIds(
  ids: string[] | undefined,
  scope: DiscountScope | undefined,
  { advanced, restrictionIds }: { advanced: boolean; restrictionIds?: string[] }
): string[] {
  if (!isModifierDiscountScope(scope)) return []
  return advanced ? [...(ids ?? [])] : [...(restrictionIds ?? [])]
}

// Selected discounted items priced at $0 under a base-item-only scope (base_item
// or its free variant). Such items have nothing to discount, so the reward
// silently won't apply to them (BUGS-4387). Only fixed-price rewards carry a
// meaningful discount scope, so non-fixed-price types (BOGO, get-a-free-item)
// never qualify. Empty array when none apply.
export function getZeroPricedBaseItems(
  scope: DiscountScope | undefined,
  key: string | undefined,
  discountedProductIds: string[],
  products: Product[]
): Product[] {
  if (!isFixedPriceKey(key)) return []
  if (getEffectiveDiscountScope(scope) !== 'base_item') return []
  return selectZeroPricedProducts(discountedProductIds, products)
}

// Selected discounted items priced at $0 under a selected-modifiers scope
// (base_item_plus_required_modifiers or its free variant) while no modifiers
// are selected. A $0 item's value comes entirely from its modifiers, so with
// none selected the reward discounts nothing. Empty array when none apply.
export function getZeroPricedItemsWithoutModifiers(
  scope: DiscountScope | undefined,
  key: string | undefined,
  discountedProductIds: string[],
  discountedModifierIds: string[],
  products: Product[]
): Product[] {
  if (!isFixedPriceKey(key)) return []
  if (getEffectiveDiscountScope(scope) !== 'base_item_plus_required_modifiers')
    return []
  if (discountedModifierIds?.length) return []
  return selectZeroPricedProducts(discountedProductIds, products)
}

function selectZeroPricedProducts(
  discountedProductIds: string[],
  products: Product[]
): Product[] {
  const selectedIds = new Set(discountedProductIds)
  return products.filter(
    product => selectedIds.has(product.chain_uid) && product.price === 0
  )
}

export const REDEEM_CATEGORIES: RedeemCategory[] = [
  {
    key: 'free_item',
    items: [
      {
        key: 'get_a_free_item',
        types: ['manual/item'],
        subtype: 'item',
      },
      {
        key: 'get_a_free_item',
        types: ['manual/item'],
        subtype: null,
      },
      {
        key: 'free_fixed_price_item',
        types: ['manual/fixed_price'],
        subtype: 'item',
      },
      {
        key: 'get_a_free_modifier',
        types: ['manual/item'],
        subtype: 'modifier',
      },
    ],
  },
  {
    key: 'discount',
    items: [
      {
        key: 'get_%_off_purchase',
        types: ['manual/percent', 'automatic/percent'],
        subtype: 'purchase',
      },
      {
        key: 'get_%_off_purchase',
        types: ['manual/percent', 'automatic/percent'],
        subtype: null,
      },
      {
        key: 'get_%_off_item',
        types: ['manual/percent'],
        subtype: 'item',
      },
      {
        key: 'get_$_off_purchase',
        types: ['manual/amount', 'automatic/amount'],
        subtype: 'purchase',
      },
      {
        key: 'get_$_off_purchase',
        types: ['manual/amount', 'automatic/amount'],
        subtype: null,
      },
      {
        key: 'get_$_off_item',
        types: ['manual/amount'],
        subtype: 'item',
      },
      {
        key: 'any_item_for_$x',
        types: ['manual/fixed_price'],
        subtype: 'item',
      },
      {
        key: 'any_modifier_for_$x',
        types: ['manual/fixed_price'],
        subtype: 'item',
      },
    ],
  },
  {
    key: 'buy_one_get_one',
    forOrdering: true,
    items: [
      {
        key: 'buy_one_get_free_one',
        types: ['manual/item'],
        subtype: 'bogo/item',
      },
      {
        key: 'buy_item_get_%_off_item',
        types: ['manual/percent'],
        subtype: 'bogo/item',
      },
      {
        key: 'buy_item_get_$_off_item',
        types: ['manual/amount'],
        subtype: 'bogo/item',
      },
    ],
  },
  {
    key: 'buy_x_get_discount',
    forOrdering: true,
    items: [
      {
        key: 'buy_x_get_%_off_purchase',
        types: ['manual/percent', 'automatic/percent'],
        subtype: 'bogo/purchase',
      },
      {
        key: 'buy_x_get_$_off_purchase',
        types: ['manual/amount', 'automatic/amount'],
        subtype: 'bogo/purchase',
      },
    ],
  },
  {
    key: 'hidden_menu',
    forOrdering: false,
    items: [
      {
        key: 'hidden_menu',
        types: ['experience/hidden_menu'],
        subtype: null,
      },
    ],
    isNonDiscount: true,
  },
  {
    key: 'golden_ticket',
    forOrdering: false,
    items: [
      {
        key: 'golden_ticket',
        types: ['experience/golden_ticket'],
        subtype: null,
      },
    ],
    isNonDiscount: true,
    isDisabled: false,
  },
  {
    key: 'bonus_points',
    forOrdering: false,
    items: [
      {
        key: 'bonus_points',
        types: ['points/static'],
        subtype: null,
      },
    ],
    isNonDiscount: true,
    isDisabled: false,
  },
  {
    key: 'delivery_discount',
    forOrdering: false,
    items: [
      {
        key: 'delivery_discount',
        types: ['manual/amount', 'manual/percent', 'manual/fixed_price'],
        subtype: 'purchase',
      },
    ],
    isNonDiscount: true,
  },
]
export const TYPE_ITEMS: RedeemTypeItem[] = flatMap(
  REDEEM_CATEGORIES,
  ({ items }) => items
)

export function getTemplateKeyForType(
  type: RedeemType,
  subtype: RedeemTemplateSubtype,
  discountScope?: DiscountScope
): string | null {
  if (type === 'manual/fixed_price' && subtype === 'item') {
    if (discountScope?.startsWith('free_')) return 'free_fixed_price_item'
    if (discountScope === 'required_modifiers') return 'any_modifier_for_$x'
    return 'any_item_for_$x'
  }
  return (
    TYPE_ITEMS.find(
      item => item.types.includes(type) && item.subtype === subtype
    )?.key ?? null
  )
}

export function getRedeemTemplateType(
  typeItem: RedeemTypeItem,
  redemptionType: InstoreRedemptionType
): RedeemType {
  const { types } = typeItem
  const isAuto = redemptionType === 'automatic_cash_back'

  const found = types.find(t => {
    if (isAuto) {
      return t === 'automatic/amount' || t === 'automatic/percent'
    }
    return t === 'manual/amount' || t === 'manual/percent'
  })
  return found || types[0]
}

export function getRedeemCategories(
  orderingEnabled: boolean
): RedeemCategory[] {
  return REDEEM_CATEGORIES.filter(
    ({ forOrdering }) => !forOrdering || orderingEnabled
  )
}

export function getDiscountCategories(
  orderingEnabled: boolean
): RedeemCategory[] {
  return getRedeemCategories(orderingEnabled).filter(
    ({ isNonDiscount }) => !isNonDiscount
  )
}

export function getNonDiscountCategories(
  orderingEnabled: boolean,
  exclude: string[] = []
): RedeemCategory[] {
  return getRedeemCategories(orderingEnabled).filter(
    ({ isNonDiscount, key }) => !!isNonDiscount && !exclude.includes(key)
  )
}

export function formatCurrency(value: number | null): string {
  const newValue = value || 0
  return I18n.l(newValue || 0, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: newValue % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })
}

export function getDiscount(
  type: RedeemType,
  discount: number | null
): string | null {
  if (type === 'manual/percent' || type === 'automatic/percent') {
    return t('percent', {
      value: discount,
    })
  }

  if (type === 'manual/amount' || type === 'automatic/amount') {
    return formatCurrency(discount)
  }

  return null
}

export function getCostToBusiness(
  type: RedeemType,
  discount: number | null
): string | null {
  if (type === 'manual/amount' || type === 'automatic/amount') {
    return null
  }

  return formatCurrency(discount)
}

export function getInstoreRedemptionType(
  template: RedeemTemplate,
  providerCouponPools?: CouponPool[]
): InstoreRedemptionType {
  const { type: templateType, uses_coupon_codes } = template

  if (!providerCouponPools && uses_coupon_codes) {
    return InstoreRedemptionType.COUPON_CODES
  }

  if (providerCouponPools && providerCouponPools?.length > 0) {
    return InstoreRedemptionType.COUPON_CODES
  }

  if (
    templateType === 'automatic/amount' ||
    templateType === 'automatic/percent'
  ) {
    return InstoreRedemptionType.CASH_BACK
  }

  return InstoreRedemptionType.MANAGER_COMP
}

function deriveDeliveryFeeMode(
  template: RedeemTemplate
): 'amount' | 'percent' | 'fixed_price' | undefined {
  if (
    template.type === 'manual/amount' &&
    template.discounted_check_section === 'delivery_fee'
  ) {
    return 'amount'
  }
  if (
    template.type === 'manual/percent' &&
    template.discounted_check_section === 'delivery_fee'
  ) {
    return 'percent'
  }
  if (
    template.type === 'manual/fixed_price' &&
    template.discount_scope === 'delivery_fee'
  ) {
    return 'fixed_price'
  }
  return undefined
}

export function generateTypeItem(
  template: RedeemTemplate
): RedeemTypeItem | null {
  if (
    (template.type === 'manual/amount' || template.type === 'manual/percent') &&
    template.discounted_check_section === 'delivery_fee'
  ) {
    return TYPE_ITEMS.find(i => i.key === 'delivery_discount') || null
  }
  if (
    template.type === 'manual/fixed_price' &&
    template.discount_scope === 'delivery_fee'
  ) {
    return TYPE_ITEMS.find(i => i.key === 'delivery_discount') || null
  }
  return (
    TYPE_ITEMS.find(({ key, types, subtype }) => {
      if (template.subtype !== subtype || !types.includes(template.type)) {
        return false
      }
      // Disambiguate fixed_price types by discount_scope
      if (
        template.type === 'manual/fixed_price' &&
        template.subtype === 'item'
      ) {
        if (template.discount_scope?.startsWith('free_')) {
          return key === 'free_fixed_price_item'
        }
        if (template.discount_scope === 'required_modifiers') {
          return key === 'any_modifier_for_$x'
        }
        return key === 'any_item_for_$x'
      }
      return true
    }) || null
  )
}

export const getInStoreValue = (
  inStoreValues: RedeemProductIdsType | undefined,
  productIds: string[] | undefined,
  productIdType: ProductIdType = 'required'
) => {
  return productIds ?? (inStoreValues?.[productIdType] || [])
}

export function mapCouponPoolToSelect(cp: CouponPool | MerchantCouponPool) {
  const date = getFormattedDate(moment(cp.created_at), 'daily')
  let label = `${date} - ${cp.type}`
  if (cp.generation_type === CouponPoolGenerationType.STATIC) {
    label = `${label} - ${cp.static_code}`
  }
  return {
    id: cp.id,
    label: label,
    value: cp.id,
    generation_type: cp.generation_type,
    type: cp.type,
    static_code: cp.static_code,
    redemption_provider: cp.redemption_provider?.value,
  }
}

export function generateFormModel(
  merchant: MerchantFields | null,
  template?: RedeemTemplate,
  couponPools?: CouponPool[] | null,
  providers?: RedemptionProvider[]
): FormModel {
  if (!template) {
    return {
      typeItem: null,
      name: '',
      description: '',
      discount: '',
      minimum: null,
      maximum: null,
      priceCap: '',
      discountScope: 'base_item',
      perceivedValue: '',
      availableOnline: !!merchant?.ordering_enabled,
      availableInstore: !merchant?.online_only,
      instoreRedemptionType: isIndirectLoyaltyIntegrationMerchant(merchant)
        ? InstoreRedemptionType.COUPON_CODES
        : InstoreRedemptionType.MANAGER_COMP,
      discountedProductIds: [],
      discountedModifierIds: [],
      requiredProductIds: [],
      instoreDiscountedProductIds: [],
      instoreDiscountedModifierIds: [],
      instoreRequiredProductIds: [],
      onlineDiscountedProductIds: [],
      onlineRequiredProductIds: [],
      onlineDiscountedModifierIds: [],
      instoreModifierDiscountIds: [],
      onlineModifierDiscountIds: [],
      instoreModifierDiscountsAdvanced: false,
      onlineModifierDiscountsAdvanced: false,
      availableCustomerSupport: true,
      multiUseRewards: getMultiUseSettings(null),
      couponCodes: {
        generationType: merchant?.supports_loyalty_integration
          ? CouponPoolGenerationType.DYNAMIC
          : CouponPoolGenerationType.VARIABLE,
        staticCode: '',
        type: merchant?.instore_scanning_enabled
          ? CouponPoolType.QRCODE
          : CouponPoolType.RAW,
        uploadColumn: null,
      },
      images: {
        [ValidationTypes.RedeemAdvertising]: {
          id: null,
          uploading: false,
          url: '',
        },
        [ValidationTypes.RedeemDetail]: {
          id: null,
          uploading: false,
          url: '',
        },
        [ValidationTypes.RedeemIndex]: {
          id: null,
          uploading: false,
          url: '',
        },
      },
      longDescription: '',
      selectedCouponPools: [],
      posFields: getPosFields({
        providers,
        couponPools,
        isSubtypeModifier: false,
      }),
      deliveryFeeMode: 'amount',
      discountedCheckSection: 'basket',
    }
  }

  const { redemption_venue } = template
  const availableOnline =
    redemption_venue === 'all' || redemption_venue === 'online'
  const availableInstore =
    !merchant?.online_only &&
    (redemption_venue === 'all' || redemption_venue === 'instore')
  const nonProviderCouponPools = (couponPools || [])
    .filter(cp => cp.redemption_provider === null)
    .map(cp => mapCouponPoolToSelect(cp))

  const typeItem = generateTypeItem(template)
  const isSubtypeModifier = typeItem?.subtype === 'modifier'
  const usesModifierDiscounts = isModifierDiscountScope(template.discount_scope)

  const onlineProductIds = template.product_ids?.digital
  const onlineDiscountedProductIds = isSubtypeModifier
    ? onlineProductIds?.discounted_related
    : onlineProductIds?.discounted
  const onlineDiscountedModifierIds = isSubtypeModifier
    ? onlineProductIds?.discounted
    : onlineProductIds?.discounted_related

  // Read in-store IDs from the provider-scoped copy for a single-POS merchant,
  // using the same predicate merchant-api mirrors on (!has_multi_pos &&
  // redemption_provider) so the form matches what the register enforces. Fall back
  // to the bare copy PER LEAF: the API serializes all three in-store leaves once any
  // provider key exists, so an empty provider leaf must not shadow a populated bare
  // one (which other readers still use) — otherwise the next save writes that empty
  // back to bare.
  const singleProviderKey =
    merchant?.has_multi_pos === false
      ? merchant?.redemption_provider?.value
      : undefined
  const providerInstore = singleProviderKey
    ? template.product_ids?.[singleProviderKey]?.instore
    : undefined
  const bareInstore = template.product_ids?.instore
  const instoreLeaf = (
    leaf:
      | 'discounted'
      | 'required'
      | 'discounted_related'
      | 'discounted_modifiers'
  ) =>
    providerInstore?.[leaf]?.length
      ? providerInstore[leaf]
      : bareInstore?.[leaf]
  const instoreDiscountedProductIds = isSubtypeModifier
    ? instoreLeaf('discounted_related')
    : instoreLeaf('discounted')
  const instoreDiscountedModifierIds = isSubtypeModifier
    ? instoreLeaf('discounted')
    : instoreLeaf('discounted_related')

  return {
    typeItem: generateTypeItem(template),
    name: template.name,
    description: template.description || '',
    discount: template.discount?.toString() || '',
    minimum:
      template.minimum != null && template.minimum > 0
        ? template.minimum.toString()
        : null,
    maximum:
      template.maximum != null && template.maximum > 0
        ? template.maximum.toString()
        : null,
    priceCap: template.price_cap != null ? template.price_cap.toString() : '',
    discountScope: template.discount_scope,
    perceivedValue: template.perceived_value?.toString() || '',
    availableOnline,
    availableInstore,
    instoreRedemptionType: getInstoreRedemptionType(template),
    discountedProductIds: template.discounted_product_ids || [],
    discountedModifierIds: template.discounted_modifier_ids || [],
    requiredProductIds: template.required_product_ids || [],
    instoreDiscountedProductIds: instoreDiscountedProductIds || [],
    instoreRequiredProductIds: instoreLeaf('required') || [],
    instoreDiscountedModifierIds: instoreDiscountedModifierIds || [],
    onlineDiscountedProductIds: onlineDiscountedProductIds || [],
    onlineRequiredProductIds: template.product_ids?.digital?.required || [],
    onlineDiscountedModifierIds: onlineDiscountedModifierIds || [],
    instoreModifierDiscountIds: instoreLeaf('discounted_modifiers') || [],
    onlineModifierDiscountIds: onlineProductIds?.discounted_modifiers || [],
    // A modifier-discount template whose two lists differ was set up in
    // advanced mode; matching lists load in the default (copied) mode
    instoreModifierDiscountsAdvanced:
      usesModifierDiscounts &&
      !sameIds(
        instoreDiscountedModifierIds,
        instoreLeaf('discounted_modifiers')
      ),
    onlineModifierDiscountsAdvanced:
      usesModifierDiscounts &&
      !sameIds(
        onlineDiscountedModifierIds,
        onlineProductIds?.discounted_modifiers
      ),
    availableCustomerSupport: template.granted_program_visible,
    hiddenMenuKey: template.unhides_category_ids
      ? [template.unhides_category_ids.toString()]
      : undefined,
    hiddenMenuName: template.category_name,
    multiUseRewards: getMultiUseSettings(template.multi_use_settings),
    couponCodes: {
      generationType:
        couponPools?.[0]?.generation_type ?? CouponPoolGenerationType.VARIABLE,
      staticCode:
        couponPools?.[0]?.generation_type === CouponPoolGenerationType.STATIC &&
        couponPools?.[0]?.static_code
          ? couponPools?.[0]?.static_code
          : '',
      type: couponPools?.[0]?.type ?? CouponPoolType.RAW,
      uploadColumn: null,
    },
    images: {
      [ValidationTypes.RedeemAdvertising]: {
        id: template.images?.advertising_image?.file_upload_id || null,
        uploading: false,
        url: template.images?.advertising_image?.small || '',
      },
      [ValidationTypes.RedeemDetail]: {
        id: template.images?.detail_image?.file_upload_id || null,
        uploading: false,
        url: template.images?.detail_image?.small || '',
      },
      [ValidationTypes.RedeemIndex]: {
        id: template.images?.index_image?.file_upload_id || null,
        uploading: false,
        url: template.images?.index_image?.small || '',
      },
    },
    longDescription: template.long_description || '',
    numberOfPoints: template.number_of_points,
    selectedCouponPools: nonProviderCouponPools,
    posFields: getPosFields({
      providers,
      template,
      couponPools,
      isSubtypeModifier,
    }),
    deliveryFeeMode: deriveDeliveryFeeMode(template),
    discountedCheckSection: template.discounted_check_section ?? 'basket',
  }
}

type getPosFieldsParamsType = {
  template?: RedeemTemplate
  couponPools?: CouponPool[] | null
  providers: RedemptionProvider[] | undefined
  isSubtypeModifier: boolean
}

export function getPosFields({
  providers,
  template,
  couponPools,
  isSubtypeModifier = false,
}: getPosFieldsParamsType): Record<string, PosFields> {
  if (!providers) return {}

  return {
    ...providers.reduce((acc, redemptionProvider) => {
      const { couponCode, formattedCouponPools } =
        mapProviderPoolsToCouponCodeDisplay(couponPools, redemptionProvider)
      const providerCouponPools = couponPools?.filter(
        pool => pool.redemption_provider?.value === redemptionProvider.value
      )

      let instoreRedemptionType

      if (redemptionProvider.integration_type === 'indirect') {
        instoreRedemptionType = InstoreRedemptionType.COUPON_CODES
      } else if (template) {
        instoreRedemptionType = getInstoreRedemptionType(
          template,
          providerCouponPools
        )
      } else {
        instoreRedemptionType = null
      }

      const instoreProductIds =
        template?.product_ids?.[redemptionProvider.value]?.instore
      const instoreDiscountedProductIds = isSubtypeModifier
        ? instoreProductIds?.discounted_related
        : instoreProductIds?.discounted
      const instoreDiscountedModifierIds = isSubtypeModifier
        ? instoreProductIds?.discounted
        : instoreProductIds?.discounted_related

      const digitalProductIds =
        template?.product_ids?.[redemptionProvider.value]?.digital
      const onlineDiscountedProductIds = isSubtypeModifier
        ? digitalProductIds?.discounted_related
        : digitalProductIds?.discounted
      const onlineDiscountedModifierIds = isSubtypeModifier
        ? digitalProductIds?.discounted
        : digitalProductIds?.discounted_related

      acc[redemptionProvider.value] = {
        instoreRedemptionType: instoreRedemptionType,
        instoreDiscountedProductIds: instoreDiscountedProductIds || [],
        instoreDiscountedModifierIds: instoreDiscountedModifierIds || [],
        instoreRequiredProductIds:
          template?.product_ids?.[redemptionProvider.value]?.instore
            ?.required || [],
        onlineDiscountedProductIds: onlineDiscountedProductIds || [],
        onlineDiscountedModifierIds: onlineDiscountedModifierIds || [],
        onlineRequiredProductIds:
          template?.product_ids?.[redemptionProvider.value]?.digital
            ?.required || [],
        instoreModifierDiscountIds:
          instoreProductIds?.discounted_modifiers || [],
        instoreModifierDiscountsAdvanced:
          isModifierDiscountScope(template?.discount_scope) &&
          !sameIds(
            instoreDiscountedModifierIds,
            instoreProductIds?.discounted_modifiers
          ),
        onlineModifierDiscountIds:
          digitalProductIds?.discounted_modifiers || [],
        couponCodes: couponCode,
        selectedCouponPools: formattedCouponPools,
      }
      return acc
    }, {}),
  }
}

export function getDiscountedProductsText(
  isItemType: boolean = false,
  subtype?: RedeemTemplateSubtype
) {
  const hasRequiredInput =
    subtype === 'bogo/item' || subtype === 'bogo/purchase'

  if (subtype === 'modifier') {
    return {
      label: t('discounted.free_modifier_item'),
      fallbackLabel: t('discounted.free_modifier_item'),
      helpText: t('discounted.free_modifier_item_help_text'),
      fallbackHelpText: t('discounted.free_modifier_item_fallback'),
    }
  }

  if (isItemType) {
    const labelKey = hasRequiredInput ? 'they_for_free' : 'free_items'
    return {
      label: t(`discounted.${labelKey}`),
      fallbackLabel: t('discounted.default_item_fallback'),
      helpText: '',
      fallbackHelpText: '',
    }
  }

  if (hasRequiredInput) {
    return {
      label: t(`discounted.they_discount`),
      fallbackLabel: t('discounted.default_item_fallback'),
      helpText: '',
      fallbackHelpText: '',
    }
  }

  return {
    label: t(`discounted.default_item_label`),
    fallbackLabel: t('discounted.default_item_fallback'),
    helpText: '',
    fallbackHelpText: '',
  }
}

export function getDiscountedModifiersText(subtype?: RedeemTemplateSubtype) {
  if (subtype === 'modifier') {
    return {
      label: t('discounted.free_modifiers'),
      helpText: t('discounted.free_modifiers_help_text'),
      secondaryHelpText: '',
      fallbackHelpText: t('discounted.free_modifier_item_fallback_help_text'),
    }
  }

  return {
    label: t('discounted.modifier_restrictions'),
    helpText: t('discounted.free_item_modifiers_help_text'),
    secondaryHelpText: t('discounted.free_item_modifiers_secondary_help_text'),
    fallbackHelpText: '',
  }
}

export function couldHaveDiscountedProducts(
  templateType?: RedeemType,
  subtype?: RedeemTemplateSubtype
) {
  return (
    templateType === 'manual/item' ||
    templateType === 'manual/fixed_price' ||
    (!!subtype && ['item', 'modifier', 'bogo/item'].includes(subtype))
  )
}

export function shouldHaveRequiredProducts(
  _templateType?: RedeemType,
  subtype?: RedeemTemplateSubtype
) {
  return !!subtype && ['bogo/item', 'bogo/purchase'].includes(subtype)
}

export const buildModifierOptionName = (modifier: Modifier) => {
  const itemNames = modifier.items
    .map(item => item.name)
    .sort((a, b) => a.localeCompare(b))
    .join(', ')
  return truncate(`${modifier.option.name} - ${itemNames}`, { length: 75 })
}

// This can be removed along with the mx-free-modifier-rewards flag when this feature is
// fully released
export function getConditionalExample(
  itemKey: string,
  freeModifierRewardsEnabled: boolean
): string {
  if (itemKey === 'get_a_free_item') {
    return freeModifierRewardsEnabled
      ? typesT('get_a_free_item.example')
      : typesT('get_a_free_item.example_without_modifiers')
  }
  return typesT(`${itemKey}.example`)
}

export function rewardIsDisplayable(template: RedeemTemplate | null) {
  const isBonusPoints = template?.type === 'points/static'
  return !isBonusPoints
}

export function splitByComma(value) {
  return value.trim().split(/\s*,\s*/)
}

export function isNDR(templateType: RedeemType): boolean {
  const ndr = REDEEM_CATEGORIES.find(({ key, isNonDiscount, items }) => {
    // delivery_discount sits on the non-discount tab but reuses the manual/*
    // template types, so it must not classify those types as non-discount —
    // that would wrongly disable coupon pools for real %/$-off discounts.
    if (!isNonDiscount || key === 'delivery_discount') return null
    return items.find(item => item.types.includes(templateType))
  })
  return !!ndr
}

export function isDirectLoyaltyIntegrationMerchant(
  merchant: MerchantFields | null
) {
  return ['toast', 'qu'].includes(merchant?.loyalty_integration_partner || '')
}

export function isDirectPosIntegration(
  redemptionProvider: RedemptionProvider | null | undefined
) {
  return redemptionProvider?.integration_type === 'direct'
}

export function isIndirectLoyaltyIntegrationMerchant(
  merchant: MerchantFields | null
) {
  return (
    !isDirectLoyaltyIntegrationMerchant(merchant) &&
    (merchant?.supports_loyalty_integration || false)
  )
}

export function isLoyaltyIntegrationPos(
  redemptionProvider: RedemptionProvider
) {
  return ['direct', 'indirect'].includes(redemptionProvider.integration_type)
}

export const getUniqueRedemptionProviders = (
  merchant: MerchantFields | null | undefined
): RedemptionProvider[] => {
  return merchant?.all_redemption_providers || []
}

export interface CouponPoolDisplayType {
  couponCode: {
    generationType: CouponPoolGenerationType
    staticCode: string
    type: CouponPoolType
    uploadColumn: number | null
  }
  formattedCouponPools: any[]
}

export const mapProviderPoolsToCouponCodeDisplay = (
  couponPools: CouponPool[] | null | undefined,
  redemptionProvider: RedemptionProvider
): CouponPoolDisplayType => {
  const providerCouponPools = couponPools?.filter(
    pool => pool.redemption_provider?.value === redemptionProvider.value
  )

  const generationType = providerCouponPools?.[0]?.generation_type
  const staticCode = providerCouponPools?.[0]?.static_code
  const couponPoolType = providerCouponPools?.[0]?.type

  const formattedCouponPools = !!providerCouponPools?.length
    ? providerCouponPools.map(cp => mapCouponPoolToSelect(cp))
    : []

  return {
    couponCode: {
      generationType: generationType ?? CouponPoolGenerationType.VARIABLE,
      staticCode:
        generationType === CouponPoolGenerationType.STATIC && staticCode
          ? staticCode
          : '',
      type: couponPoolType ?? CouponPoolType.RAW,
      uploadColumn: null,
    },
    formattedCouponPools: formattedCouponPools,
  }
}

export const getMultiUseSettings = (
  multiUseSettings: MultiUseSettings | null | undefined
): MultiUseRewardsSettings => {
  if (
    multiUseSettings?.schema ===
    'Program::Issuance::MultiUseStrategies::EveryXDaysStrategy'
  ) {
    return {
      usageType: 'multiple_times',
      limitType:
        multiUseSettings.limit_condition?.max_issuances_per_window &&
        multiUseSettings.limit_condition?.window_duration
          ? 'set_number'
          : 'unlimited',
      maxIssuances: multiUseSettings.end_condition.max_issuances,
      activeWindowDuration:
        multiUseSettings.end_condition.active_window_duration / (24 * 60 * 60),
      maxIssuancesPerWindow: multiUseSettings.limit_condition
        ? multiUseSettings.limit_condition?.max_issuances_per_window
        : undefined,
      windowDuration: multiUseSettings.limit_condition
        ? multiUseSettings.limit_condition?.window_duration / (24 * 60 * 60)
        : undefined,
    }
  }

  return {
    usageType: 'exactly_once',
  }
}

export const calculateEstimatedMaxValue = (
  {
    maxIssuances,
    maxIssuancesPerWindow,
    activeWindowDuration,
    windowDuration,
  }: MultiUseRewardsSettings,
  discount: string | null
): number | null => {
  const maxWindows =
    activeWindowDuration && windowDuration
      ? Math.ceil(activeWindowDuration / windowDuration)
      : null
  if (maxWindows && maxIssuancesPerWindow) {
    return maxIssuances
      ? Math.min(maxIssuances, maxIssuancesPerWindow * maxWindows) *
          Number(discount)
      : maxIssuancesPerWindow * maxWindows * Number(discount)
  } else if (maxIssuances) {
    return maxIssuances * Number(discount)
  }

  return null
}

export function getMinMaxVisibility({
  templateMinMaxEnabled,
  isDeliveryDiscount,
  isBonusPoints,
  isExperience,
  isDollarOff,
}: {
  templateMinMaxEnabled: boolean
  isDeliveryDiscount: boolean
  isBonusPoints: boolean
  isExperience: boolean
  isDollarOff: boolean
}) {
  const supportsMinMax =
    templateMinMaxEnabled && !isBonusPoints && !isExperience
  return {
    showMinimumSpend: isDeliveryDiscount || supportsMinMax,
    showMaximumDiscount: supportsMinMax && !isDollarOff,
  }
}
